/**
 * Admin Analytics — TASK 8-2.
 *
 * Owner-admin dashboard. KPI cards + charts + operational tables.
 *
 * Hard rules (TASK 8-2):
 *   • DO NOT expose this page in any nav element when isOwnerAdmin === false.
 *     Belt-and-suspenders: this component still hard-blocks render below
 *     when the JWT email is not the owner-admin so a hand-typed URL can't
 *     leak data.
 *   • KPI cards + charts read from `kpi_snapshots` (cheap, RLS-protected),
 *     never from raw tables. The operational tables at the bottom of the
 *     page DO read raw tables — but only via existing admin RLS policies
 *     (job_applications_ggj, referral_events) or a SECURITY DEFINER RPC
 *     (admin_outreach_needed_users) gated to the owner-admin email.
 *
 * Data fetched:
 *   • kpi_snapshots — last 200 days, all metrics (one query).
 *   • job_applications_ggj — top 50 most-recent.
 *   • referral_events with commission_amount > 0 — top 50 most-recent.
 *   • admin_outreach_needed_users(50) — RPC.
 */

import {
  ShieldAlert,
  RefreshCw,
  LineChart as LineChartIcon,
  Users,
  Activity as ActivityIcon,
  TrendingUp,
  TrendingDown,
  Wallet,
  UserPlus,
  Zap,
  Award,
  AlertCircle,
  FileText,
  Mail,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { DataTable } from '../../../components/DataTable';
import type { Column } from '../../../components/DataTable';
import { useAuthentication } from '../../../hooks/useAuthentication';
import { supabase } from '../../../lib/supabase';
import { OWNER_EMAIL } from './Jobs/helpers';
import Layout, { type DashboardPage } from './Layout';
import { AdminBarChart, chartHasValues } from './Analytics/AdminBarChart';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

interface KpiRow {
  date:        string;        // YYYY-MM-DD
  metric_name: string;
  value:       number;
  metadata:    Record<string, unknown> | null;
}

interface ApplicationRow extends Record<string, unknown> {
  id:               string;
  career_post_id:   string;
  applicant_name:   string;
  applicant_email:  string;
  applicant_phone:  string | null;
  status:           string;
  applied_at:       string;
}

interface CommissionRow extends Record<string, unknown> {
  id:                 string;
  code_id:            string;
  referred_user_id:   string;
  event_type:         'signup' | 'first_payment' | 'renewal';
  payment_amount:     number | null;
  commission_amount:  number | null;
  paid_out:           boolean;
  paid_out_at:        string | null;
  created_at:         string;
}

interface OutreachRow extends Record<string, unknown> {
  user_id:    string;
  name:       string;
  email:      string;
  created_at: string;
  days_idle:  number;
}

interface TopReferralEntry {
  code:          string;
  owner_user_id: string;
  signups:       number;
}

interface SubscriberRow extends Record<string, unknown> {
  user_id:         string;
  email:           string | null;
  kind:            'subscription' | 'credit_pack';
  plan:            string | null;
  amount_inr:      number | null;
  credits:         number | null;
  status:          string;
  period_months:   number | null;
  next_billing_at: string | null;
  created_at:      string;
}

interface KpiBundle {
  /** Map of metric_name → row of the latest available date. */
  latest:   Map<string, KpiRow>;
  /** All rows in the trailing window, newest last. */
  all:      KpiRow[];
  /** Date string of the latest snapshot (YYYY-MM-DD), or null. */
  asOf:     string | null;
}

interface LiveRevenueTrendPoint {
  date:  string;
  value: number;
}

interface LiveRevenueMetrics {
  revenue_30d_inr: number;
  trend:             LiveRevenueTrendPoint[];
}

const SNAPSHOT_WINDOW_DAYS = 200;
const FETCH_TIMEOUT_MS = 20_000;

type AdminAnalyticsTab = 'analytics' | 'applications' | 'commissions' | 'outreach';

const NEXT_ACTION_TO_PATH: Record<string, string> = {
  'prepare-for-interview': '/prepare-for-interview',
  'ai-companion':          '/ai-companion',
  'collaborate':           '/collaborate',
  'dashboard':             '/dashboard',
  'history':               '/history',
  'resume-builder':        '/resume-builder',
  'profile':               '/profile',
  'credits':               '/credits',
  'support':               '/support',
  'calendar-notes':        '/calendar-notes',
  'jobs':                  '/jobs',
  // Deprecated route — redirect to /agent (single-optimize was retired).
  'single-optimize':       '/agent',
  'developer':             '/developers',
  'skill-assessment':      '/profile',
};

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function fmtInr(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

function fmtInt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN');
}

function shortDateLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Group KpiRows by metric_name and return the most recent N entries
 * (oldest first) for the requested metric. Useful for time-series charts.
 */
function metricSeries(all: KpiRow[], metric: string, lastN: number): KpiRow[] {
  const filtered = all
    .filter((r) => r.metric_name === metric)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (filtered.length <= lastN) return filtered;
  return filtered.slice(filtered.length - lastN);
}

/**
 * Down-sample a time series to roughly `target` points by averaging into
 * equal-width buckets. Keeps the leading/trailing dates so the chart range
 * stays correct. Used to keep the 6-month MRR chart readable (≤ 12 bars).
 */
function downsample(series: KpiRow[], target: number): { labels: string[]; data: number[] } {
  if (series.length === 0) return { labels: [], data: [] };
  if (series.length <= target) {
    return {
      labels: series.map((r) => shortDateLabel(r.date)),
      data:   series.map((r) => Math.round(r.value)),
    };
  }
  const bucketSize = Math.ceil(series.length / target);
  const labels: string[] = [];
  const data:   number[] = [];
  for (let i = 0; i < series.length; i += bucketSize) {
    const slice = series.slice(i, i + bucketSize);
    const avg = slice.reduce((acc, r) => acc + Number(r.value || 0), 0) / slice.length;
    labels.push(shortDateLabel(slice[slice.length - 1].date));
    data.push(Math.round(avg));
  }
  return { labels, data };
}

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────

export interface AdminAnalyticsProps {
  onSignOut?: () => void | Promise<void>;
  /** Skip the Layout shell (sidebar + header) when rendered inside another page */
  embedded?: boolean;
}

const AdminAnalytics: React.FC<AdminAnalyticsProps> = ({ onSignOut = () => {}, embedded = false }) => {
  const { session, user, loading: authLoading } = useAuthentication();

  const isAdmin = useMemo<boolean>(() => {
    const email = (user?.email ?? session?.user?.email ?? '').toLowerCase();
    return email === OWNER_EMAIL.toLowerCase();
  }, [user?.email, session?.user?.email]);

  const [activeTab,       setActiveTab]     = useState<AdminAnalyticsTab>('analytics');
  const [bundle,        setBundle]       = useState<KpiBundle | null>(null);
  const [applications,  setApplications] = useState<ApplicationRow[]>([]);
  const [commissions,   setCommissions]  = useState<CommissionRow[]>([]);
  const [outreach,      setOutreach]     = useState<OutreachRow[]>([]);
  const [subscribers,   setSubscribers]  = useState<SubscriberRow[]>([]);
  const [liveRevenue,   setLiveRevenue]  = useState<LiveRevenueMetrics | null>(null);
  const [loading,       setLoading]      = useState(false);
  const [error,         setError]        = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(true);

  const sessionUserId = session?.user?.id ?? null;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Layout-level navigation handler (mirrors AgentPage / ReferralPage) ──
  const handleNavigate = useCallback((page: DashboardPage) => {
    const target = NEXT_ACTION_TO_PATH[page] || '/dashboard';
    window.history.pushState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  // ── Data load (once per mount; Refresh re-runs) ──
  const loadAll = useCallback(async () => {
    if (!sessionUserId || !isAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const timeout = new Promise<never>((_, reject) => {
      window.setTimeout(
        () => reject(new Error('Request timed out. Click Refresh to try again.')),
        FETCH_TIMEOUT_MS,
      );
    });

    try {
      const sinceDate = new Date(Date.now() - SNAPSHOT_WINDOW_DAYS * 24 * 3600 * 1000)
        .toISOString()
        .slice(0, 10);

      const fetchAll = Promise.all([
        supabase
          .from('kpi_snapshots')
          .select('date, metric_name, value, metadata')
          .gte('date', sinceDate)
          .order('date', { ascending: true }),
        supabase
          .from('job_applications_ggj')
          .select('id, career_post_id, applicant_name, applicant_email, applicant_phone, status, applied_at')
          .order('applied_at', { ascending: false })
          .limit(50),
        supabase
          .from('referral_events')
          .select('id, code_id, referred_user_id, event_type, payment_amount, commission_amount, paid_out, paid_out_at, created_at')
          .not('commission_amount', 'is', null)
          .gt('commission_amount', 0)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase.rpc('admin_outreach_needed_users', { p_limit: 50 }).then(
          res => res,
          () => ({ data: [], error: null }),
        ),
        supabase.rpc('admin_recent_subscribers', { p_limit: 50 }).then(
          res => res,
          () => ({ data: [], error: null }),
        ),
        supabase.rpc('admin_live_revenue_metrics', { p_days: 180 }).then(
          res => res,
          () => ({ data: null, error: null }),
        ),
      ]);

      const [kpiRes, appsRes, commRes, outreachRes, subsRes, revenueRes] = await Promise.race([fetchAll, timeout]);
      if (!mountedRef.current) return;

      if (kpiRes.error)  throw kpiRes.error;
      if (appsRes.error) throw appsRes.error;
      if (commRes.error) throw commRes.error;
      if (outreachRes.error) {
        console.warn('[AdminAnalytics] admin_outreach_needed_users RPC unavailable:', outreachRes.error.message);
      }
      if (subsRes.error) {
        console.warn('[AdminAnalytics] admin_recent_subscribers RPC unavailable:', subsRes.error.message);
      }
      if (revenueRes.error) {
        console.warn('[AdminAnalytics] admin_live_revenue_metrics RPC unavailable:', revenueRes.error.message);
      }

      const kpiRows = (kpiRes.data ?? []) as KpiRow[];
      const latestDate = kpiRows.length > 0
        ? kpiRows.reduce((acc, r) => (r.date > acc ? r.date : acc), kpiRows[0].date)
        : null;
      const latest = new Map<string, KpiRow>();
      if (latestDate) {
        for (const r of kpiRows) {
          if (r.date === latestDate) latest.set(r.metric_name, r);
        }
      }

      setBundle({ latest, all: kpiRows, asOf: latestDate });
      setApplications((appsRes.data ?? []) as ApplicationRow[]);
      setCommissions((commRes.data ?? []) as CommissionRow[]);
      setOutreach((outreachRes.data ?? []) as OutreachRow[]);
      setSubscribers((subsRes.data ?? []) as SubscriberRow[]);

      const rawRevenue = revenueRes.data as Record<string, unknown> | null;
      if (rawRevenue && Array.isArray(rawRevenue.trend)) {
        setLiveRevenue({
          revenue_30d_inr: Number(rawRevenue.revenue_30d_inr ?? 0),
          trend: (rawRevenue.trend as LiveRevenueTrendPoint[]).map((p) => ({
            date:  String(p.date),
            value: Number(p.value ?? 0),
          })),
        });
      } else {
        setLiveRevenue(null);
      }
    } catch (e) {
      if (!mountedRef.current) return;
      const msg = e instanceof Error ? e.message : 'Could not load admin analytics.';
      setError(msg);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [sessionUserId, isAdmin]);

  const handleRefresh = useCallback(async () => {
    if (!sessionUserId || !isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      const { error: cronErr } = await supabase.rpc('admin_invoke_cron_job', {
        p_jobname: 'compute-kpis-daily',
      });
      if (cronErr) {
        console.warn('[AdminAnalytics] KPI recompute skipped:', cronErr.message);
      } else {
        await new Promise((resolve) => window.setTimeout(resolve, 2500));
      }
    } catch (e) {
      console.warn('[AdminAnalytics] KPI recompute failed:', e);
    }

    await loadAll();
  }, [sessionUserId, isAdmin, loadAll]);

  useEffect(() => {
    if (!sessionUserId || !isAdmin) {
      setLoading(false);
      return;
    }
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    void loadAll();
  }, [sessionUserId, isAdmin, loadAll]);

  // ── KPI lookups ──
  const kpi = useCallback((m: string): number | null => {
    const v = bundle?.latest.get(m)?.value;
    return v == null ? null : Number(v);
  }, [bundle]);

  // ── Chart series (memoised) ──
  const userGrowth90d = useMemo(() => {
    if (!bundle) return { labels: [], data: [] };
    const series = metricSeries(bundle.all, 'new_users_7d', 90);
    return downsample(series, 12);
  }, [bundle]);

  // Prefer live payment data (purchase + subscription). Fall back to snapshots.
  const revenueSixMonth = useMemo(() => {
    if (liveRevenue?.trend?.length) {
      const series: KpiRow[] = liveRevenue.trend.map((p) => ({
        date:        p.date,
        metric_name: 'revenue_30d_inr',
        value:       p.value,
        metadata:    null,
      }));
      return downsample(series, 12);
    }

    if (!bundle) return { labels: [], data: [] };
    const byDate = new Map<string, KpiRow>();
    for (const r of bundle.all) {
      if (r.metric_name !== 'mrr_inr') continue;
      const src = String(r.metadata?.source ?? '');
      if (src.includes('normalized_monthly') || src.includes('pack_subscriptions')) continue;
      byDate.set(r.date, r);
    }
    for (const r of bundle.all) {
      if (r.metric_name === 'revenue_30d_inr') byDate.set(r.date, r);
    }
    const merged = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
    const lastN = merged.length <= 180 ? merged : merged.slice(merged.length - 180);
    return downsample(lastN, 12);
  }, [bundle, liveRevenue]);

  const revenue30dLive = liveRevenue?.revenue_30d_inr ?? kpi('revenue_30d_inr');

  const optsThirtyDay = useMemo(() => {
    if (!bundle) return { labels: [], data: [] };
    const series = metricSeries(bundle.all, 'optimizations_today', 30);
    return downsample(series, 12);
  }, [bundle]);

  const topReferrals = useMemo<{ labels: string[]; data: number[] }>(() => {
    const meta = bundle?.latest.get('top_referral_codes_30d')?.metadata as
      | { top?: TopReferralEntry[] }
      | null
      | undefined;
    const top = Array.isArray(meta?.top) ? meta!.top : [];
    return {
      labels: top.map((t) => t.code),
      data:   top.map((t) => Number(t.signups || 0)),
    };
  }, [bundle]);

  // ── DataTable columns ──
  const applicationCols = useMemo<Column<ApplicationRow>[]>(() => [
    { key: 'applied_at',      header: 'Date',    minWidth: 150,
      cell: (v) => new Date(String(v)).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) },
    { key: 'applicant_name',  header: 'Name',    minWidth: 160 },
    { key: 'applicant_email', header: 'Email',   minWidth: 200 },
    { key: 'status',          header: 'Status',  minWidth: 110,
      cell: (v) => <StatusPill label={String(v)} /> },
  ], []);

  const commissionCols = useMemo<Column<CommissionRow>[]>(() => [
    { key: 'created_at',         header: 'Date',     minWidth: 150,
      cell: (v) => new Date(String(v)).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) },
    { key: 'event_type',         header: 'Event',    minWidth: 130,
      cell: (v) => <EventBadge type={String(v) as CommissionRow['event_type']} /> },
    { key: 'payment_amount',     header: 'Payment',  minWidth: 120,
      cell: (v) => fmtInr(Number(v)) },
    { key: 'commission_amount',  header: 'Commission', minWidth: 130,
      cell: (v) => fmtInr(Number(v)) },
    { key: 'paid_out',           header: 'Status',   minWidth: 110,
      cell: (v) => (v ? <PaidPill /> : <PendingPill />) },
  ], []);

  const subscriberCols = useMemo<Column<SubscriberRow>[]>(() => [
    { key: 'created_at',      header: 'Paid on',  minWidth: 150,
      cell: (v) => new Date(String(v)).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) },
    { key: 'email',           header: 'User',     minWidth: 220,
      cell: (v) => String(v ?? '—') },
    { key: 'plan',            header: 'Plan',     minWidth: 150,
      cell: (v) => <StatusPill label={String(v ?? '—')} /> },
    { key: 'kind',            header: 'Type',     minWidth: 130,
      cell: (v) => (String(v) === 'subscription' ? 'Subscription' : 'Credit pack') },
    { key: 'amount_inr',      header: 'Amount',   minWidth: 120,
      cell: (v) => fmtInr(Number(v)) },
    { key: 'credits',         header: 'Credits',  minWidth: 100,
      cell: (v) => (v == null ? '—' : fmtInt(Number(v))) },
    { key: 'status',          header: 'Status',   minWidth: 120,
      cell: (v) => <StatusPill label={String(v)} /> },
    { key: 'next_billing_at', header: 'Next bill', minWidth: 150,
      cell: (v) => (v ? new Date(String(v)).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—') },
  ], []);

  const payingUsersSummary = useMemo(() => {
    const uniqueUsers = new Set(subscribers.map((s) => s.user_id)).size;
    const totalCollected = subscribers.reduce((acc, s) => acc + (Number(s.amount_inr) || 0), 0);
    return { uniqueUsers, totalCollected };
  }, [subscribers]);

  const outreachStats = useMemo(() => {
    const critical = outreach.filter((r) => r.days_idle > 30).length;
    const warning = outreach.filter((r) => r.days_idle > 14 && r.days_idle <= 30).length;
    const recent = outreach.filter((r) => r.days_idle <= 14).length;
    return { total: outreach.length, critical, warning, recent };
  }, [outreach]);

  const exportOutreachCsv = useCallback(() => {
    const headers = ['Name', 'Email', 'Signed up', 'Days idle'];
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = [
      headers.join(','),
      ...outreach.map((r) =>
        [r.name, r.email, shortDateLabel(r.created_at.slice(0, 10)), String(r.days_idle)]
          .map((v) => esc(String(v)))
          .join(','),
      ),
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'outreach-needed.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [outreach]);

  const outreachCols = useMemo<Column<OutreachRow>[]>(() => [
    {
      key: 'name',
      header: 'User',
      minWidth: 220,
      alwaysVisible: true,
      cell: (_v, row) => <OutreachUserCell name={row.name} />,
    },
    {
      key: 'email',
      header: 'Email',
      minWidth: 240,
      cell: (v) => (v ? (
        <a
          href={`mailto:${String(v)}`}
          className="ds-aa-email-link"
          onClick={(e) => e.stopPropagation()}
        >
          {String(v)}
        </a>
      ) : (
        <span className="ds-aa-text-muted text-[12px] italic">No email</span>
      )),
    },
    {
      key: 'created_at',
      header: 'Signed up',
      minWidth: 140,
      cell: (v) => (
        <span className="ds-aa-cell-date">
          {new Date(String(v)).toLocaleDateString(undefined, { dateStyle: 'medium' })}
        </span>
      ),
    },
    {
      key: 'days_idle',
      header: 'Days idle',
      minWidth: 120,
      cell: (v) => <IdleSeverityPill days={Number(v)} />,
    },
  ], []);

  const Wrap = ({ children }: { children: React.ReactNode }) =>
    embedded ? <>{children}</> : (
      <Layout currentPage="profile" onNavigate={handleNavigate} onSignOut={onSignOut}>
        {children}
      </Layout>
    );

  // ── Render gates ──
  const ANALYTICS_TABS: { id: AdminAnalyticsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'analytics',     label: 'Analytics',            icon: <LineChartIcon className="w-3.5 h-3.5" /> },
    { id: 'applications',  label: 'Recent Applications',  icon: <FileText className="w-3.5 h-3.5" /> },
    { id: 'commissions',   label: 'Referral Commission',  icon: <Award className="w-3.5 h-3.5" /> },
    { id: 'outreach',      label: 'Outreach needed',      icon: <Mail className="w-3.5 h-3.5" /> },
  ];

  if (authLoading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner size="lg" showText={false} />
        </div>
      </Wrap>
    );
  }

  if (!session) {
    return (
      <Wrap>
        <ForbiddenCard
          title="Sign in required"
          message="Sign in with the owner-admin account to view this page."
        />
      </Wrap>
    );
  }

  if (!isAdmin) {
    return (
      <Wrap>
        <ForbiddenCard
          title="Access denied"
          message="This page is restricted to the owner-admin account."
        />
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="ds-admin-analytics px-2 sm:px-4 lg:px-6 py-4 sm:py-6 max-w-[1280px] mx-auto w-full min-w-0">
        {/* ── Header ── */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: 'var(--brand-purple-soft, rgba(108,71,255,0.10))', color: 'var(--brand-purple, #6C47FF)' }}>
            <LineChartIcon className="w-5 h-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="ggj-page-title text-2xl sm:text-3xl font-bold leading-tight"
                style={{ color: 'var(--vscode-text-primary)' }}>
              Admin Analytics
            </h1>
            <p className="text-[13px] sm:text-sm mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
              {bundle?.asOf
                ? `Snapshot as of ${new Date(`${bundle.asOf}T00:00:00Z`).toLocaleDateString(undefined, { dateStyle: 'medium' })}`
                : 'Daily KPI snapshot pipeline (TASK 8-1) feeds these tiles.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleRefresh()}
            className="min-h-[40px] inline-flex items-center gap-1.5 px-3 rounded-xl text-[13px] font-medium touch-manipulation
              vscode-bg-tertiary hover:vscode-bg-hover vscode-text-primary border vscode-border transition-colors"
            aria-label="Refresh analytics"
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {error ? (
          <div className="rounded-xl px-3 py-2 mb-3 text-[13px]"
               style={{ background: 'var(--vscode-error-bg, rgba(239,68,68,0.08))', color: 'var(--vscode-error, #b91c1c)', border: '1px solid var(--vscode-error-border, rgba(239,68,68,0.25))' }}>
            <span className="inline-flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" aria-hidden /> {error}
            </span>
          </div>
        ) : null}

        {/* ── Sub-tabs (mirrors Control Panel) ── */}
        <div
          className="ds-aa-tabs flex gap-1 border-b mb-4 overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0 scrollbar-thin"
          style={{ borderColor: 'var(--ds-divider, var(--vscode-border-secondary))', WebkitOverflowScrolling: 'touch' }}
          role="tablist"
        >
          {ANALYTICS_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`ds-aa-tab inline-flex items-center gap-1.5 flex-shrink-0 min-h-[44px] px-3 py-2 text-xs sm:text-[13px] font-medium transition-colors relative whitespace-nowrap touch-manipulation ${
                activeTab === tab.id ? 'ds-aa-tab--active' : ''
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Panels stay mounted — switching tabs does not refetch */}
        <div className={activeTab === 'analytics' ? 'block' : 'hidden'} aria-hidden={activeTab !== 'analytics'}>
          <SectionSpinner loading={loading} minHeight={480}>
            <section
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5"
              aria-label="Top KPI cards"
            >
              <KpiCard label="WAU" value={fmtInt(kpi('wau'))} icon={Users} loading={false} />
              <KpiCard label="MAU" value={fmtInt(kpi('mau'))} icon={ActivityIcon} loading={false} />
              <KpiCard label="MRR" value={fmtInr(kpi('mrr_inr'))} icon={Wallet} loading={false} highlight />
              <KpiCard label="Revenue (30d)" value={fmtInr(revenue30dLive)} icon={Wallet} loading={false} />
              <KpiCard label="Churn" value={fmtPct(kpi('churn_rate'))} icon={TrendingDown} loading={false} negativeWhenAbove={0.05} numeric={kpi('churn_rate')} />
              <KpiCard label="New users 7d" value={fmtInt(kpi('new_users_7d'))} icon={UserPlus} loading={false} />
              <KpiCard label="New users 30d" value={fmtInt(kpi('new_users_30d'))} icon={TrendingUp} loading={false} />
              <KpiCard label="Total optimizations" value={fmtInt(kpi('total_optimizations'))} icon={Zap} loading={false} />
              <KpiCard label="Commissions owed" value={fmtInr(kpi('referral_commissions_owed_inr'))} icon={Award} loading={false} />
            </section>

            <section className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
              <ChartCard
                title="User growth — last 90 days"
                subtitle="Rolling 7d new-user count, sampled across the window."
                data={userGrowth90d}
                color="#6C47FF"
                showValues
              />
              <ChartCard
                title={`Revenue trend${revenueSixMonth.labels.length >= 2 ? ` — ${revenueSixMonth.labels[0]} to ${revenueSixMonth.labels[revenueSixMonth.labels.length - 1]}` : ''}`}
                subtitle="Live trailing-30d revenue from completed payments (credit packs + subscriptions)."
                data={revenueSixMonth}
                color="#22C55E"
                showValues
                formatValue={(n) => fmtInr(n).replace(/\.\d+$/, '')}
              />
              <ChartCard
                title="Optimization volume — last 30 days"
                subtitle="Resume optimizations completed per day."
                data={optsThirtyDay}
                color="#22D3EE"
              />
              <ChartCard
                title="Top referral codes — last 30 days"
                subtitle="Signups attributed to each code (top 5)."
                data={topReferrals}
                color="#F59E0B"
                emptyMessage="No referral signups yet."
              />
            </section>

            <section className="mt-6">
              <TableSection
                title={`Paying users${subscribers.length ? ` — ${payingUsersSummary.uniqueUsers} users · ${fmtInr(payingUsersSummary.totalCollected).replace(/\.\d+$/, '')} collected` : ''}`}
                subtitle="Users who paid — credit-pack purchases and subscriptions (latest 50)."
              >
                <DataTable<SubscriberRow>
                  columns={subscriberCols}
                  rows={subscribers}
                  loading={loading}
                  ariaLabel="Paying users"
                  emptyMessage="No payments yet."
                  pageSizeOptions={[10, 25, 50]}
                  initialPageSize={10}
                  reserveFloatingSpace
                />
              </TableSection>
            </section>
          </SectionSpinner>
        </div>

        <div className={activeTab === 'applications' ? 'block' : 'hidden'} aria-hidden={activeTab !== 'applications'}>
          <TableSection
            title="Recent applications"
            subtitle="Latest 50 GoGrabJob career applications."
          >
            <DataTable<ApplicationRow>
              columns={applicationCols}
              rows={applications}
              loading={loading}
              ariaLabel="Recent applications"
              emptyMessage="No applications yet."
              pageSizeOptions={[10, 25, 50]}
              initialPageSize={10}
              reserveFloatingSpace
            />
          </TableSection>
        </div>

        <div className={activeTab === 'commissions' ? 'block' : 'hidden'} aria-hidden={activeTab !== 'commissions'}>
          <TableSection
            title="Referral commissions"
            subtitle="Latest 50 paid/pending commission events."
          >
            <DataTable<CommissionRow>
              columns={commissionCols}
              rows={commissions}
              loading={loading}
              ariaLabel="Referral commissions"
              emptyMessage="No commissions yet."
              pageSizeOptions={[10, 25, 50]}
              initialPageSize={10}
              reserveFloatingSpace
            />
          </TableSection>
        </div>

        <div className={activeTab === 'outreach' ? 'ds-admin-analytics-outreach block' : 'hidden'} aria-hidden={activeTab !== 'outreach'}>
          <AdminTablePanel
            icon={<Mail className="w-4 h-4" aria-hidden />}
            title="Outreach needed"
            subtitle="Users signed up more than 7 days ago who have never optimized a resume."
            stats={[
              { label: `${outreachStats.total} users`, tone: 'brand' as const },
              ...(outreachStats.critical > 0
                ? [{ label: `${outreachStats.critical} critical (30d+)`, tone: 'danger' as const }]
                : []),
              ...(outreachStats.warning > 0
                ? [{ label: `${outreachStats.warning} follow-up (15–30d)`, tone: 'warn' as const }]
                : []),
              ...(outreachStats.recent > 0
                ? [{ label: `${outreachStats.recent} recent (≤14d)`, tone: 'default' as const }]
                : []),
            ]}
            onExport={outreach.length > 0 ? exportOutreachCsv : undefined}
          >
            <DataTable<OutreachRow>
              columns={outreachCols}
              rows={outreach}
              loading={loading}
              ariaLabel="Outreach-needed users"
              emptyMessage="Nobody is overdue for outreach — great retention."
              pageSizeOptions={[10, 25, 50]}
              initialPageSize={10}
              reserveFloatingSpace
              headerLeft={
                <span className="ds-aa-table-count">
                  {loading ? 'Loading…' : `${outreach.length} accounts`}
                </span>
              }
            />
          </AdminTablePanel>
        </div>
      </div>
    </Wrap>
  );
};

export default AdminAnalytics;

// ─────────────────────────────────────────────────────────────────────────
// Sub-components (kept local so the file is self-contained)
// ─────────────────────────────────────────────────────────────────────────

/** Centered spinner overlay for KPI/chart sections while data loads. */
function SectionSpinner({
  loading,
  children,
  minHeight = 320,
}: {
  loading: boolean;
  children: React.ReactNode;
  minHeight?: number;
}) {
  return (
    <div
      className="relative isolate w-full rounded-xl"
      style={{ minHeight: loading ? minHeight : undefined }}
    >
      {loading && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-xl"
          style={{
            background: 'color-mix(in srgb, var(--surface-card, var(--vscode-bg-elevated)) 90%, transparent)',
          }}
          role="status"
          aria-live="polite"
          aria-label="Loading"
        >
          <LoadingSpinner size="lg" showText={false} />
        </div>
      )}
      <div className={loading ? 'opacity-30 pointer-events-none select-none' : undefined}>
        {children}
      </div>
    </div>
  );
}

interface KpiCardProps {
  label:    string;
  value:    string;
  icon:     React.ComponentType<{ className?: string }>;
  loading?: boolean;
  highlight?: boolean;
  /** Optional numeric value — when set, applies "negative" coloring above the threshold. */
  numeric?:           number | null;
  negativeWhenAbove?: number;
}

function KpiCard({ label, value, icon: Icon, loading, highlight, numeric, negativeWhenAbove }: KpiCardProps) {
  const isNegative = negativeWhenAbove != null && numeric != null && numeric > negativeWhenAbove;

  return (
    <div
      className="rounded-xl px-3 py-2.5 min-h-[88px] flex flex-col justify-between"
      style={{
        background: highlight
          ? 'var(--brand-purple-soft, rgba(108,71,255,0.10))'
          : 'var(--surface-card, var(--vscode-bg-elevated))',
        border: highlight
          ? '1px solid var(--brand-purple, #6C47FF)'
          : '1px solid var(--vscode-border-primary)',
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-widest"
              style={{ color: highlight ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-text-tertiary)' }}>
          {label}
        </span>
        <Icon className="w-3.5 h-3.5 shrink-0"
              aria-hidden
              style={{ color: highlight ? 'var(--brand-purple, #6C47FF)' : 'var(--vscode-text-tertiary)' }} />
      </div>
      <div
        className={`text-xl sm:text-2xl font-bold mt-1 ${loading ? 'animate-pulse opacity-60' : ''}`}
        style={{
          color: isNegative
            ? '#dc2626'
            : highlight
              ? 'var(--brand-purple, #6C47FF)'
              : 'var(--vscode-text-primary)',
        }}
      >
        {loading ? '—' : value}
      </div>
    </div>
  );
}

interface ChartCardProps {
  title:        string;
  subtitle:     string;
  data:         { labels: string[]; data: number[] };
  color:        string;
  emptyMessage?: string;
  showValues?:  boolean;
  formatValue?: (value: number) => string;
}

const KPI_EMPTY_HINT =
  'Click Refresh to recompute KPIs from live payment data, then reload snapshots.';

function ChartCard({ title, subtitle, data, color, emptyMessage, showValues, formatValue }: ChartCardProps) {
  const hasValues = chartHasValues(data);
  const isEmpty = data.labels.length === 0;

  return (
    <div
      className="flex min-h-0 flex-col overflow-hidden rounded-2xl p-4"
      style={{
        background: 'var(--surface-card, var(--vscode-bg-elevated))',
        border:     '1px solid var(--vscode-border-primary)',
      }}
    >
      <div className="mb-3 shrink-0">
        <h3 className="text-base font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>{title}</h3>
        <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-tertiary)' }}>{subtitle}</p>
      </div>
      <div className="min-h-0 w-full shrink-0">
        {isEmpty ? (
          <div
            className="flex h-[248px] items-center justify-center rounded-xl border px-4 text-center text-[13px]"
            style={{
              color: 'var(--vscode-text-tertiary)',
              borderColor: 'var(--vscode-border-secondary)',
              background: 'var(--vscode-bg-secondary, rgba(255,255,255,0.03))',
            }}
          >
            <div>
              <p>{emptyMessage ?? 'No data yet.'}</p>
              <p className="text-[11px] mt-1 opacity-80">{KPI_EMPTY_HINT}</p>
            </div>
          </div>
        ) : (
          <AdminBarChart
            data={data}
            color={color}
            height={200}
            emptyMessage={emptyMessage ?? 'No data for this period.'}
            emptyHint={!hasValues ? KPI_EMPTY_HINT : undefined}
            showValues={showValues}
            formatValue={formatValue}
          />
        )}
      </div>
    </div>
  );
}

function TableSection({
  title,
  subtitle,
  children,
}: {
  title:    string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <AdminTablePanel title={title} subtitle={subtitle}>
      {children}
    </AdminTablePanel>
  );
}

type StatTone = 'default' | 'brand' | 'warn' | 'danger';

function AdminTablePanel({
  title,
  subtitle,
  children,
  icon,
  stats,
  onExport,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  stats?: { label: string; tone?: StatTone }[];
  onExport?: () => void;
}) {
  return (
    <div className="ds-admin-table-panel">
      <div className="ds-admin-table-panel__header">
        {icon ? (
          <span className="ds-icon-square shrink-0" aria-hidden>
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h2 className="ds-aa-text-strong text-base font-semibold">{title}</h2>
          <p className="ds-aa-text-muted text-[12px] mt-0.5">{subtitle}</p>
          {stats && stats.length > 0 ? (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {stats.map((s) => (
                <span
                  key={s.label}
                  className={`ds-admin-table-stat${
                    s.tone === 'brand' ? ' ds-admin-table-stat--brand'
                    : s.tone === 'warn' ? ' ds-admin-table-stat--warn'
                    : s.tone === 'danger' ? ' ds-admin-table-stat--danger'
                    : ''
                  }`}
                >
                  {s.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {onExport ? (
          <button
            type="button"
            onClick={onExport}
            className="tbtn shrink-0"
            aria-label="Export outreach list as CSV"
          >
            Export CSV
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

const AVATAR_COLORS = ['#6C47FF', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

function OutreachUserCell({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
  const bg = AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];

  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
        style={{ background: bg }}
        aria-hidden
      >
        {initials}
      </span>
      <span className="ds-aa-text-strong truncate font-medium">{name}</span>
    </span>
  );
}

function IdleSeverityPill({ days }: { days: number }) {
  const tone = days > 30 ? 'danger' : days > 14 ? 'warn' : 'info';

  return (
    <span className={`ds-aa-meta-chip ds-aa-meta-chip--${tone}`}>
      <span className={`ds-aa-meta-chip__dot ds-aa-meta-chip__dot--${tone}`} aria-hidden />
      {fmtInt(days)}d
    </span>
  );
}

function ForbiddenCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="px-4 py-10 max-w-md mx-auto">
      <div
        className="rounded-2xl p-6 text-center"
        style={{
          background: 'var(--surface-card, var(--vscode-bg-elevated))',
          border:     '1px solid var(--vscode-border-primary)',
        }}
      >
        <div className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-3"
             style={{ background: 'rgba(239,68,68,0.12)', color: '#b91c1c' }}>
          <ShieldAlert className="w-6 h-6" aria-hidden />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>{title}</h2>
        <p className="text-[13px] mt-1" style={{ color: 'var(--vscode-text-secondary)' }}>{message}</p>
      </div>
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  const tone = (() => {
    switch (label) {
      case 'shortlisted': return { bg: 'rgba(16,185,129,0.14)', color: '#047857' };
      case 'hired':       return { bg: 'rgba(16,185,129,0.20)', color: '#065f46' };
      case 'rejected':    return { bg: 'rgba(239,68,68,0.12)',  color: '#b91c1c' };
      case 'reviewed':    return { bg: 'rgba(59,130,246,0.12)', color: '#1d4ed8' };
      default:            return { bg: 'rgba(108,71,255,0.10)', color: '#6C47FF' };
    }
  })();
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold capitalize"
          style={{ background: tone.bg, color: tone.color }}>
      {label}
    </span>
  );
}

function EventBadge({ type }: { type: CommissionRow['event_type'] }) {
  const styleMap: Record<CommissionRow['event_type'], { bg: string; color: string; label: string }> = {
    signup:        { bg: 'rgba(108,71,255,0.10)', color: '#6C47FF', label: 'Signup' },
    first_payment: { bg: 'rgba(16,185,129,0.14)', color: '#047857', label: 'First payment' },
    renewal:       { bg: 'rgba(16,185,129,0.20)', color: '#065f46', label: 'Renewal' },
  };
  const s = styleMap[type];
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold whitespace-nowrap"
          style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function PaidPill() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold"
          style={{ background: 'rgba(16,185,129,0.15)', color: '#047857' }}>
      Paid out
    </span>
  );
}

function PendingPill() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold"
          style={{ background: 'rgba(245,158,11,0.15)', color: '#b45309' }}>
      Pending
    </span>
  );
}
